import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/card';
import ProgressBar from '../components/ProgressBar';
import PageHeader from '../components/PageHeader';
import { supabase } from '../lib/supabase';
import { flavorImageMap, defaultFlavorMeta, colorToTailwind } from '../lib/productMeta';

const NO_FLAVOR_OPTION = {
  id: 'no-flavor',
  name: 'Geen extra smaak',
  description: 'Puur genieten',
  color: 'from-muted/20 to-muted/10',
  image: null
};

export const FlavorSelection = ({ order, setOrder }) => {
  const navigate = useNavigate();
  const [flavors, setFlavors] = useState([]);
  const [loading, setLoading] = useState(true);
  const productId = order.product?.id;

  const fetchFlavors = useCallback(async () => {
    if (!productId) {
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from('product_flavors')
      .select('flavor_id, flavors(*)')
      .eq('product_id', productId);

    if (error) {
      setLoading(false);
      return;
    }

    const enriched = data.map(pf => {
      const f = pf.flavors;
      // Look up by Supabase ID - names come from DB
      const meta = flavorImageMap[f.id] || defaultFlavorMeta;
      const color = meta.tailwindColor || colorToTailwind[f.color] || defaultFlavorMeta.tailwindColor;
      return {
        id: f.id,
        name: f.name,         // Always from Supabase
        description: meta.description,
        color,
        image: meta.image
      };
    });

    enriched.push(NO_FLAVOR_OPTION);
    setFlavors(enriched);
    setLoading(false);
  }, [productId]);

  useEffect(() => {
    fetchFlavors();
  }, [fetchFlavors]);

  const handleSelect = (flavor) => {
    setOrder({ ...order, flavor });
    navigate('/burger-option');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <ProgressBar currentStep={3} totalSteps={6} />
      <PageHeader 
        title="Kies je smaak"
        subtitle="Selecteer je favoriete basis smaak"
      />

      <div className="container max-w-6xl mx-auto px-6 mt-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {flavors.map((flavor, index) => (
            <motion.div
              key={flavor.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
            >
              <Card
                onClick={() => handleSelect(flavor)}
                data-testid={`flavor-card-${flavor.id}`}
                className={`product-card p-6 cursor-pointer border-2 bg-card hover:border-primary touch-feedback min-h-[240px] flex flex-col relative ${
                  order.flavor?.id === flavor.id ? 'border-primary bg-primary/5' : 'border-border'
                }`}
              >
                {order.flavor?.id === flavor.id && (
                  <div className="absolute top-4 right-4 w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Check className="w-5 h-5 text-primary-foreground" />
                  </div>
                )}

                <div className={`w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center mb-4 bg-gradient-to-br ${flavor.color}`}>
                  {flavor.image ? (
                    <img src={flavor.image} alt={flavor.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-3xl text-muted-foreground">{'\u2014'}</span>
                  )}
                </div>
                
                <h3 className="text-xl font-bold text-foreground mb-2">{flavor.name}</h3>
                <p className="text-sm text-muted-foreground">{flavor.description}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FlavorSelection;
